import { useState, FormEvent } from 'react';

interface RegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'agency' | 'strategic';
}

export default function RegistrationModal({ isOpen, onClose, type }: RegistrationModalProps) {
  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    phone: '',
    email: '',
    address: '',
    experience: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  if (!isOpen) return null;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('partnershipType', type === 'agency' ? 'Đại Lý Phổ Thông' : 'Đối Tác Chiến Lược');
      formBody.append('fullName', formData.fullName);
      formBody.append('companyName', formData.companyName);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('address', formData.address);
      formBody.append('experience', formData.experience);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d4hrtdker93evkarrs3g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          fullName: '',
          companyName: '',
          phone: '',
          email: '',
          address: '',
          experience: '',
          message: ''
        });
        setTimeout(() => {
          window.location.href = 'https://thankyou.phuotmy.com/';
        }, 1000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className={`p-8 text-white ${type === 'agency' ? 'bg-gradient-to-r from-blue-600 to-blue-700' : 'bg-gradient-to-r from-amber-500 to-amber-600'}`}>
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-3xl font-bold mb-2">
                {type === 'agency' ? 'Đăng Ký Đại Lý Phổ Thông' : 'Đăng Ký Đối Tác Chiến Lược'}
              </h3>
              <p className="text-white/90">Điền thông tin để chúng tôi liên hệ với bạn</p>
            </div>
            <button 
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full hover:bg-white/30 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
        </div>

        <form id="registration-form" onSubmit={handleSubmit} data-readdy-form className="p-8">
          <div className="space-y-6">
            <div>
              <label className="block text-gray-700 font-semibold mb-2">
                Họ và Tên <span className="text-red-500">*</span>
              </label>
              <input 
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                placeholder="Nhập họ và tên của bạn"
              />
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-2">
                Tên Công Ty / Tổ Chức <span className="text-red-500">*</span>
              </label>
              <input 
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                placeholder="Nhập tên công ty"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Số Điện Thoại <span className="text-red-500">*</span>
                </label>
                <input 
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                  placeholder="0912 345 678"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input 
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                  placeholder="email@example.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-2">
                Địa Chỉ
              </label>
              <input 
                type="text"
                name="address"
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                placeholder="Địa chỉ công ty"
              />
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-2">
                Kinh Nghiệm Du Lịch
              </label>
              <select
                name="experience"
                value={formData.experience}
                onChange={(e) => setFormData({...formData, experience: e.target.value})}
                className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all cursor-pointer"
              >
                <option value="">Chọn mức độ kinh nghiệm</option>
                <option value="Mới bắt đầu">Mới bắt đầu</option>
                <option value="1-3 năm">1-3 năm</option>
                <option value="3-5 năm">3-5 năm</option>
                <option value="Trên 5 năm">Trên 5 năm</option>
              </select>
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-2">
                Lời Nhắn
              </label>
              <textarea 
                name="message"
                value={formData.message}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setFormData({...formData, message: e.target.value});
                  }
                }}
                maxLength={500}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all resize-none"
                placeholder="Chia sẻ mong muốn hợp tác của bạn..."
              ></textarea>
              <p className="text-sm text-gray-500 mt-1">{formData.message.length}/500 ký tự</p>
            </div>

            {submitStatus === 'success' && (
              <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center gap-2">
                <i className="ri-check-line text-xl"></i>
                <span>Đăng ký thành công! Chúng tôi sẽ liên hệ với bạn sớm.</span>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
                <i className="ri-error-warning-line text-xl"></i>
                <span>Có lỗi xảy ra. Vui lòng thử lại sau.</span>
              </div>
            )}

            <div className="flex gap-4">
              <button 
                type="submit"
                disabled={isSubmitting}
                className={`flex-1 px-8 py-4 ${type === 'agency' ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800' : 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700'} text-white font-bold rounded-full transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isSubmitting ? 'Đang gửi...' : 'Đăng Ký Ngay'}
              </button>
              <button 
                type="button"
                onClick={onClose}
                className="px-8 py-4 bg-gray-200 text-gray-700 font-bold rounded-full hover:bg-gray-300 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Đóng
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
